import { useDataProvider, useRecordContext } from "ra-core";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { Client, ClientType } from "../types";
import { validateClientRfc } from "./clientValidation";

// Almikia Epic E3b, owner answer A7: the check waits for typing to settle
// before firing, so it never fires a request per keystroke.
const CHECK_DEBOUNCE_MS = 400;

/**
 * Warns, without blocking, when another client already holds the RFC
 * currently entered in the client create/edit form (Almikia Epic E3b, owner
 * answer A7). Must be called from inside a react-hook-form `<Form>` (the
 * client create/edit forms both provide one).
 *
 * The check only ever fires for a syntactically valid RFC (reuses
 * `validateClientRfc`, the same validator the field itself uses) -- an
 * empty or malformed value never reaches the data provider. It is debounced
 * so a fast typist never fires one request per keystroke, and it excludes
 * the record's own id (via `useRecordContext`, present while editing, absent
 * while creating) so editing a client never warns about its own RFC.
 */
export const useRfcDuplicateCheck = (): boolean => {
  const dataProvider = useDataProvider();
  const record = useRecordContext<Client>();
  const { control } = useFormContext();
  const rfc = useWatch({ control, name: "rfc" }) as string | null | undefined;
  const clientType = useWatch({ control, name: "client_type" }) as
    | ClientType
    | undefined;
  const [isDuplicate, setIsDuplicate] = useState(false);

  useEffect(() => {
    const isValidRfc =
      validateClientRfc(rfc, { client_type: clientType }) === undefined;
    if (!rfc || !isValidRfc) {
      setIsDuplicate(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      dataProvider
        .checkClientRfcDuplicate(rfc, record?.id)
        .then((duplicate: boolean) => {
          if (!cancelled) setIsDuplicate(duplicate);
        })
        .catch(() => {
          // Non-blocking by design: a failed check never blocks the form,
          // it just skips the warning.
          if (!cancelled) setIsDuplicate(false);
        });
    }, CHECK_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rfc, clientType, record?.id, dataProvider]);

  return isDuplicate;
};
